// React 18: shadcn's current templates are React-19 style (ref is a plain
// prop, no forwardRef). Every wrapper forwards its ref here, or Radix's
// Slot/Presence (asChild triggers, portal overlays) can't reach the DOM
// node — focus return and exit animations break and React warns.
// Regenerate + re-apply (projects/tailwind-migration).
import * as React from "react";
import { forwardRef } from "react";
("use client");

import { cn } from "@/lib/utils";
import { AlertDialog as AlertDialogPrimitive } from "radix-ui";

import { Button } from "@/components/ui/button";

const AlertDialog = forwardRef(function AlertDialog({ ...props }, ref) {
  return (
    <AlertDialogPrimitive.Root ref={ref} data-slot="alert-dialog" {...props} />
  );
});

const AlertDialogTrigger = forwardRef(function AlertDialogTrigger(
  { ...props },
  ref,
) {
  return (
    <AlertDialogPrimitive.Trigger
      ref={ref}
      data-slot="alert-dialog-trigger"
      {...props}
    />
  );
});

const AlertDialogPortal = forwardRef(function AlertDialogPortal(
  { ...props },
  ref,
) {
  return (
    <AlertDialogPrimitive.Portal
      ref={ref}
      data-slot="alert-dialog-portal"
      {...props}
    />
  );
});

const AlertDialogOverlay = forwardRef(function AlertDialogOverlay(
  { className, ...props },
  ref,
) {
  return (
    <AlertDialogPrimitive.Overlay
      ref={ref}
      data-slot="alert-dialog-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
});

const AlertDialogContent = forwardRef(function AlertDialogContent(
  { className, size = "default", onOpenAutoFocus, onCloseAutoFocus, ...props },
  ref,
) {
  // Hand-written on top of the generated component, as in ui/dialog.jsx:
  // Radix returns focus to an AlertDialogTrigger, and these alerts are
  // controlled with none, so every close dropped focus on <body>. Hand it
  // back to whoever had focus when the alert opened — unless focus has
  // already gone somewhere on purpose (a delete that focused what
  // replaced the deleted thing). A consumer's own handler runs first and
  // may preventDefault to choose.
  const openerRef = React.useRef(null);
  return (
    <AlertDialogPortal ref={ref}>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        data-size={size}
        className={cn(
          "group/alert-dialog-content fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-background p-6 shadow-lg duration-200 data-[size=sm]:max-w-xs data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[size=default]:sm:max-w-lg",
          className,
        )}
        onOpenAutoFocus={(event) => {
          openerRef.current = document.activeElement;
          onOpenAutoFocus?.(event);
        }}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          if (event.defaultPrevented) return;
          event.preventDefault();
          const now = document.activeElement;
          const moved =
            now && now !== document.body && now.isConnected && !now.closest?.("[role=alertdialog]");
          if (!moved) openerRef.current?.focus?.();
        }}
        {...props}
      />
    </AlertDialogPortal>
  );
});

const AlertDialogHeader = forwardRef(function AlertDialogHeader(
  { className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      data-slot="alert-dialog-header"
      className={cn(
        "grid grid-rows-[auto_1fr] place-items-center gap-1.5 text-center has-data-[slot=alert-dialog-media]:grid-rows-[auto_auto_1fr] has-data-[slot=alert-dialog-media]:gap-x-6 sm:group-data-[size=default]/alert-dialog-content:place-items-start sm:group-data-[size=default]/alert-dialog-content:text-left sm:group-data-[size=default]/alert-dialog-content:has-data-[slot=alert-dialog-media]:grid-rows-[auto_1fr]",
        className,
      )}
      {...props}
    />
  );
});

const AlertDialogFooter = forwardRef(function AlertDialogFooter(
  { className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      data-slot="alert-dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 group-data-[size=sm]/alert-dialog-content:grid group-data-[size=sm]/alert-dialog-content:grid-cols-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
});

const AlertDialogTitle = forwardRef(function AlertDialogTitle(
  { className, ...props },
  ref,
) {
  return (
    <AlertDialogPrimitive.Title
      ref={ref}
      data-slot="alert-dialog-title"
      className={cn(
        "text-lg font-semibold sm:group-data-[size=default]/alert-dialog-content:group-has-data-[slot=alert-dialog-media]/alert-dialog-content:col-start-2",
        className,
      )}
      {...props}
    />
  );
});

const AlertDialogDescription = forwardRef(function AlertDialogDescription(
  { className, ...props },
  ref,
) {
  return (
    <AlertDialogPrimitive.Description
      ref={ref}
      data-slot="alert-dialog-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
});

const AlertDialogMedia = forwardRef(function AlertDialogMedia(
  { className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      data-slot="alert-dialog-media"
      className={cn(
        "mb-2 inline-flex size-16 items-center justify-center rounded-md bg-muted sm:group-data-[size=default]/alert-dialog-content:row-span-2 *:[svg:not([class*='size-'])]:size-8",
        className,
      )}
      {...props}
    />
  );
});

const AlertDialogAction = forwardRef(function AlertDialogAction(
  { className, variant = "default", size = "default", ...props },
  ref,
) {
  return (
    <Button ref={ref} variant={variant} size={size} asChild>
      <AlertDialogPrimitive.Action
        data-slot="alert-dialog-action"
        className={cn(className)}
        {...props}
      />
    </Button>
  );
});

const AlertDialogCancel = forwardRef(function AlertDialogCancel(
  { className, variant = "outline", size = "default", ...props },
  ref,
) {
  return (
    <Button ref={ref} variant={variant} size={size} asChild>
      <AlertDialogPrimitive.Cancel
        data-slot="alert-dialog-cancel"
        className={cn(className)}
        {...props}
      />
    </Button>
  );
});

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
};
